import {
  DashboardState,
  EDatasourceType,
  EFormFieldSource,
  IActionDefinitionDto,
  ICRUDFormDiMetadata,
  ICRUDFormField,
  ICreateWorkflowStartScreenResponse,
  IFormMetadataByStep,
  IFormSection,
  IProcessStepScreensInfo,
  IProject,
  IScreenDesignInfo,
  IStartProcessTrigger,
  IUpdateWorkflowScreensProgress,
  IUpdateWorkflowScreensResponse,
  IWFParameter,
  IWFStep,
  IWorkFlow,
  IWorkflowScreenSettings
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { Guid } from "guid-typescript";
import { notifyDesignerLiveToolIdle } from "../../../../services/designer-live/designer-live";
import { FormService } from "../../../../services/form-service";
import { WorkflowScreenService } from "../../../../services/workflow-screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import type { ActionFilterContext } from "../../../../shared/utility/action-definition-filter";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import {
  AssignScreenDropdown,
  IPendingUnmanagedAssignment
} from "../../../common/step-screen-form-wizard/step-screen-form-wizard-assign-screen-dropdown";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import { KuikaSkeletonTable } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinner from "../../../ui-kit/kuika-spinner/kuika-spinner";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import { failureMessageOr, toastService } from "../../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { defaultElementTypeFor } from "./form-element-type-options";
import {
  consumePendingFormsInteractions,
  consumePendingFormsStep,
  subscribeFormsInteractions,
  subscribeFormsStep
} from "./forms-interactions-focus-bus";
import FormsTabAddFieldModal, { IAddFieldParamOption } from "./forms-tab-add-field-modal";
import { resetFormsTabDirty, setFormsTabDirty } from "./forms-tab-dirty-state";
import {
  ensureFormSectionIds,
  IApplyResultPartition,
  IApplyStepAdoption,
  IApplyStepFailure,
  partitionApplyResults,
  reconcileFormBoParams,
  selectApplyEntries
} from "./forms-tab-fields";
import FormsTabForm from "./forms-tab-form";
import FormsTabInteractions from "./forms-tab-interactions";
import { listSelectableOptionActions } from "./forms-tab-options-source";
import FormsTabScreenSettings from "./forms-tab-screen-settings";
import FormsTabSidebar, { IStepScreenLabel, StepScreenState } from "./forms-tab-sidebar";
import {
  getStepOutputParameterIds,
  getStepOutputParams,
  getUserTaskSteps,
  needsStartScreen,
  startScreenNameFor
} from "./forms-tab-steps";
import "./forms-tab.scss";

const SUBTAB_SCREENS = "Workflow Screens";
const SUBTAB_INTERACTIONS = "Interactions";

/**
 * Test-id for one of the two Forms sub-tabs, e.g. "Workflow Screens" -> `processFormsSubtab-workflow-screens`.
 *
 * Same shape and reason as `processEditorTabTestId`: rc-segmented paints a radio input over each
 * option and forwards no arbitrary props, so an option needs a per-option hook for the owning
 * `<label>` to be found by. `toLowerCase` is locale-independent in JS, so the id is stable under a
 * Turkish locale.
 */
const subTabTestId = (subTab: string): string => `processFormsSubtab-${subTab.toLowerCase().replace(/\s+/g, "-")}`;

export interface IFormsTabProps {}

export interface IFormsTabState {
  subTab: string;
  selectedStepId: string | null;
  metadataByStep: IFormMetadataByStep[];
  /**
   * Step ids with un-applied changes (form edits AND interaction edits — both alter the generated
   * screen). Selecting a step merely loads its metadata into `metadataByStep`; only dirty steps
   * (plus drafts) are sent on Apply Screens, so browsing the sidebar never regenerates untouched
   * screens.
   */
  dirtySteps: Set<string>;
  /** Step ids the user explicitly chose to build a new (managed) form for — a not-yet-applied draft. */
  draftSteps: Set<string>;
  /** Existing screens picked for screenless steps, linked as unmanaged on Apply Screens. */
  pendingAssignments: IPendingUnmanagedAssignment[];
  loadingStepId: string | null;
  /** True while "Create All Screens" is drafting every screenless step (KOA-18273). */
  isBulkCreating: boolean;
  isApplying: boolean;
  /** Blocking-overlay caption while applying, fed by the stream's per-step progress events. */
  applyProgress: string | null;
  addFieldOpen: boolean;
  /** Section the Add Field picker targets (set when the picker opens). */
  addFieldSectionId: string | null;
  /** Whether the project-wide workflow-screen settings modal is open. */
  screenSettingsOpen: boolean;
  /**
   * The post-Apply offer to generate a start screen: the process takes input parameters but no
   * StartProcess action in the project targets it (KOA-18746). No closes it; it is asked again on
   * the next successful Apply while that still holds.
   */
  startScreenPromptOpen: boolean;
  /** True while the generate-start-screen request runs (the Yes button's spinner). */
  isGeneratingStartScreen: boolean;
}

/**
 * The Forms tab: the step-screen wizard reborn for a single process. The left rail lists this
 * process's user tasks; selecting one loads its form metadata and renders an editable section
 * table. "Apply Screens" pushes only the EDITED forms (plus created drafts and pending screen
 * assignments) through the same backend the legacy wizard used, scoped to the open process —
 * merely visiting a step must never regenerate its screen.
 */
class FormsTab extends PureSdComponent<IFormsTabProps, IFormsTabState> {
  /** Unsubscribe from the interactions-focus bus (property-panel "Manage interactions" link). */
  private unsubscribeFocus?: () => void;

  /** Unsubscribe from the step-focus bus (property-panel "SET UP" screen CTA). */
  private unsubscribeStepFocus?: () => void;

  /**
   * Signature of the process's parameter list at the last reconcile. The tab stays MOUNTED across
   * process-editor tab switches now, so "reconcile re-runs on remount" no longer holds — a param
   * added on the Parameters tab must be folded into already-loaded forms when it changes.
   */
  private lastParamSignature: string = "";

  constructor(props: IFormsTabProps & IWithSDProps) {
    super(props);
    this.state = {
      subTab: SUBTAB_SCREENS,
      selectedStepId: null,
      metadataByStep: [],
      dirtySteps: new Set(),
      draftSteps: new Set(),
      pendingAssignments: [],
      loadingStepId: null,
      isBulkCreating: false,
      isApplying: false,
      applyProgress: null,
      addFieldOpen: false,
      addFieldSectionId: null,
      screenSettingsOpen: false,
      startScreenPromptOpen: false,
      isGeneratingStartScreen: false
    };
  }

  componentDidMount(): void {
    this.lastParamSignature = this.paramSignature();
    this.publishDirtyState();
    this.ensureSelection();
    // The Diagram property-panel link asks us to open Interactions for a specific step.
    this.unsubscribeFocus = subscribeFormsInteractions((stepId: string): void => {
      this.setState({ subTab: SUBTAB_INTERACTIONS, selectedStepId: stepId });
    });
    // The property panel's "SET UP" CTA asks us to open a step on the Workflow Screens sub-tab;
    // selectStep (not a bare setState) so the step's form metadata loads as if clicked.
    this.unsubscribeStepFocus = subscribeFormsStep((stepId: string): void => {
      this.setState({ subTab: SUBTAB_SCREENS });
      this.selectStep(stepId);
    });
    // The links also switch the process-editor tab (which mounts us), so a request may have fired
    // before we subscribed — consume any retained one now.
    const pending: string | undefined = consumePendingFormsInteractions();
    if (pending) this.setState({ subTab: SUBTAB_INTERACTIONS, selectedStepId: pending });
    const pendingStep: string | undefined = consumePendingFormsStep();
    if (pendingStep) {
      this.setState({ subTab: SUBTAB_SCREENS });
      this.selectStep(pendingStep);
    }
  }

  componentWillUnmount(): void {
    this.unsubscribeFocus?.();
    this.unsubscribeStepFocus?.();
    // A tab that is gone holds nothing. No idle callback: the caller's own teardown applies
    // whatever the designer-live gate was deferring.
    resetFormsTabDirty();
  }

  componentDidUpdate(prevProps: IFormsTabProps & IWithSDProps): void {
    // Before the early return below, so the busy flag is published on EVERY update path.
    this.publishDirtyState();
    const prevId: string | undefined = prevProps.sd.selectedWf?.id;
    const nextId: string | undefined = this.props.sd.selectedWf?.id;
    if (prevId !== nextId) {
      // Switched process — drop everything loaded for the old one, then re-seed selection.
      this.lastParamSignature = this.paramSignature();
      this.setState(
        {
          selectedStepId: null,
          metadataByStep: [],
          dirtySteps: new Set(),
          draftSteps: new Set(),
          pendingAssignments: [],
          // The offer was about the process we just left.
          startScreenPromptOpen: false
        },
        (): void => this.ensureSelection()
      );
      return;
    }
    this.reconcileOnParamChange();
    this.ensureSelection();
  }

  /**
   * Fingerprint of everything the GENERATED screens depend on from the process definition: every
   * parameter's identity and shape (forms bind BO params by id; ResumeProcess actions reference
   * internals by name; validation checks both) plus each user task's output mapping.
   */
  private paramSignature = (): string => {
    const params: string = (this.workflow?.parameters ?? [])
      .map(
        (p: IWFParameter): string =>
          `${p.id}:${p.name}:${String(p.dataType)}:${String(p.cardinality ?? "")}:${p.isBusinessObject ? 1 : 0}:${
            p.isObject ? 1 : 0
          }`
      )
      .join("|");
    const outputs: string = this.steps
      .map((s: IWFStep): string => `${s.id}=${[...getStepOutputParameterIds(s)].join(",")}`)
      .join("|");
    return `${params}#${outputs}`;
  };

  /**
   * A parameter or output-mapping change makes every managed step's GENERATED screen stale on the
   * server (its form fields, ResumeProcess params, and validation all derive from the process
   * definition) — the backend only heals a screen when its step goes through Apply. So on a
   * signature change: (1) fold missing BO reference rows into already-loaded forms
   * (`reconcileFormBoParams` only APPENDS, so user edits are untouched), and (2) queue every
   * managed/draft step for the next Apply, loading metadata for steps never visited so they are
   * sendable. Without (2), a removed/renamed parameter left stale action params on the generated
   * screens forever — project validation kept flagging them and the dirty-scoped Apply offered no
   * way to regenerate an "unedited" step.
   */
  private reconcileOnParamChange = (): void => {
    const signature: string = this.paramSignature();
    if (signature === this.lastParamSignature) return;
    this.lastParamSignature = signature;
    for (const entry of this.state.metadataByStep) {
      const step: IWFStep | undefined = this.steps.find((s: IWFStep): boolean => s.id === entry.stepId);
      const reconciled: ICRUDFormDiMetadata = reconcileFormBoParams(
        entry.formMetadata,
        this.businessObjectParams,
        getStepOutputParameterIds(step)
      );
      if (reconciled !== entry.formMetadata) {
        this.setMetadata(entry.stepId, reconciled, this.state.dirtySteps.has(entry.stepId));
      }
    }
    for (const step of this.steps) {
      const state: StepScreenState = this.stepState(step.id);
      if (state !== "managed" && state !== "draft") continue;
      if (!this.getMetadataFor(step.id)) {
        // Mark dirty AFTER the load lands — the fresh server copy stores itself as clean.
        void this.loadStepMetadata(step.id).then((loaded: boolean): void => {
          if (loaded) this.markStepDirty(step.id);
        });
      } else {
        this.markStepDirty(step.id);
      }
    }
  };

  /**
   * How much unapplied work the tab is holding: edited/draft step forms plus pending screen
   * assignments. Drives both the Apply button's counter and the designer-live busy flag, so the
   * number the user sees and the number the refresh gate trusts can never disagree.
   */
  private get pendingApplyCount(): number {
    return (
      selectApplyEntries(this.state.metadataByStep, this.state.dirtySteps, this.state.draftSteps).length +
      this.state.pendingAssignments.length
    );
  }

  /**
   * Persist the project-wide workflow-screen settings.
   *
   * Writes through `updateProject`, the same path the retired wizard used, so the project-meta
   * diff and the collab-safe patch apply unchanged. The value only takes visible effect when a
   * screen next goes through Apply Screens — the modal says so, and nothing is regenerated here.
   */
  private handleSaveScreenSettings = (settings: IWorkflowScreenSettings): void => {
    const project: IProject | undefined = this.props.sd.project;
    if (!project) return;

    // Closed FIRST, then saved. Holding the dialog open until a network call resolves is what a
    // user reported as "Save does nothing": a save that hangs or rejects left the dialog stuck,
    // and with it no way to change the setting again. The dialog is a chooser, not a transaction —
    // and a failure is reported rather than expressed as a frozen dialog.
    this.setState({ screenSettingsOpen: false });

    // The project object is MUTATED and passed by reference, which is how every other writer of
    // this setting does it (the workflow DSL's own action, and the retired wizard). The provider
    // diffs the passed object against its previous revision and then adopts it as the live
    // project, so handing it a spread COPY makes the copy the live project — and every holder of
    // the previous reference is silently looking at a different object.
    project.workflowScreensSettings = { ...project.workflowScreensSettings, ...settings };

    void this.props.sd.updateProject(project).catch((error: unknown): void => KMainFunctions.exceptionHandler(error));
  };

  /**
   * Tell designer-live whether this tab is busy.
   *
   * Published from the lifecycle rather than from `render` so it stays a side-effect-free render.
   * On the transition back to CLEAN it also reports that the tool went idle, which applies a
   * reload that was deferred while the user was mid-edit — the same courtesy the retired wizard
   * modal did on close.
   */
  private publishDirtyState = (): void => {
    setFormsTabDirty(this.pendingApplyCount > 0, notifyDesignerLiveToolIdle);
  };

  private get workflow(): IWorkFlow | undefined {
    return this.props.sd.selectedWf;
  }

  private get steps(): IWFStep[] {
    return getUserTaskSteps(this.workflow);
  }

  /** Interactions on the selected step — the Interactions sub-tab badge count (interactions are per-step). */
  private get interactionCount(): number {
    return this.selectedStep?.userInteractions?.length ?? 0;
  }

  /** Steps with a screen mapped (managed or unmanaged) — the Workflow Screens sub-tab badge count. */
  private get mappedScreenCount(): number {
    return this.steps.filter((s: IWFStep): boolean => !!this.boundScreen(s.id)?.screenId).length;
  }

  private get selectedStep(): IWFStep | undefined {
    return this.steps.find((step: IWFStep): boolean => step.id === this.state.selectedStepId);
  }

  /** Selects the first step (loading its form) whenever nothing valid is selected yet. */
  private ensureSelection = (): void => {
    const steps: IWFStep[] = this.steps;
    if (steps.length === 0) return;
    const current: string | null = this.state.selectedStepId;
    const stillValid: boolean = !!current && steps.some((step: IWFStep): boolean => step.id === current);
    if (!stillValid) {
      this.selectStep(steps[0].id);
    }
  };

  private selectStep = (stepId: string): void => {
    this.setState({ selectedStepId: stepId });
    // Only a managed screen or an in-progress draft renders a form — load its metadata. A screenless
    // step shows the create/assign choice instead, so we don't generate a form behind the user's back.
    const state: StepScreenState = this.stepState(stepId);
    if ((state === "managed" || state === "draft") && !this.getMetadataFor(stepId)) {
      void this.loadStepMetadata(stepId);
    }
  };

  /** What this Apply sends: the edited and drafted steps' form metadata. */
  private buildApplyEntries = (): IFormMetadataByStep[] =>
    selectApplyEntries(this.state.metadataByStep, this.state.dirtySteps, this.state.draftSteps);

  private pendingFor = (stepId: string): IPendingUnmanagedAssignment | undefined =>
    this.state.pendingAssignments.find((p: IPendingUnmanagedAssignment): boolean => p.stepId === stepId);

  /**
   * The screen state of a step. A persisted binding wins (managed / unmanaged); otherwise a
   * user-started draft, then a pending existing-screen assignment, else nothing bound.
   */
  private stepState = (stepId: string): StepScreenState => {
    const bound: IProcessStepScreensInfo | undefined = this.boundScreen(stepId);
    if (bound?.screenId) return bound.isManaged ? "managed" : "unmanaged";
    if (this.state.draftSteps.has(stepId)) return "draft";
    if (this.pendingFor(stepId)) return "pending";
    return "none";
  };

  private screenName = (screenId: string | undefined): string => {
    if (!screenId) return "Screen";
    return this.props.sd.screenList?.find((s: IScreenDesignInfo): boolean => s.id === screenId)?.name ?? "Screen";
  };

  private getMetadataFor = (stepId: string): ICRUDFormDiMetadata | undefined =>
    this.state.metadataByStep.find((entry: IFormMetadataByStep): boolean => entry.stepId === stepId)?.formMetadata;

  /** The screen ids the given apply entries map to — the managed screens this Apply regenerates. */
  private screenIdsFor = (entries: readonly IFormMetadataByStep[]): Set<string> => {
    const ids: Set<string> = new Set();
    for (const entry of entries) {
      const screenId: string | undefined = this.boundScreen(entry.stepId)?.screenId;
      if (screenId) ids.add(screenId);
    }
    return ids;
  };

  private boundScreen = (stepId: string): IProcessStepScreensInfo | undefined => {
    const processId: string | undefined = this.workflow?.id;
    if (!processId) return undefined;
    return this.props.sd.project?.processStepScreenIds
      ?.filter((ps: IProcessStepScreensInfo): boolean => ps.processId === processId && ps.stepId === stepId)
      ?.at(-1);
  };

  /** Loads a step's form metadata into `metadataByStep`. Resolves true only when metadata landed. */
  private loadStepMetadata = async (stepId: string): Promise<boolean> => {
    const processId: string | undefined = this.workflow?.id;
    if (!processId) return false;
    this.setState({ loadingStepId: stepId });
    try {
      const metadata: ICRUDFormDiMetadata | undefined = await this.fetchStepMetadata(processId, stepId);
      if (metadata) {
        // A managed screen keeps its saved field selection; a fresh draft pre-selects the step's outputs.
        const bound: IProcessStepScreensInfo | undefined = this.boundScreen(stepId);
        const isManaged: boolean = !!(bound?.screenId && bound.isManaged);
        const base: ICRUDFormDiMetadata = isManaged ? metadata : this.normalizeDraftFields(metadata, stepId);
        // Whether managed or draft, guarantee every current BO param is on the form: one added to
        // the process after this form was generated is auto-appended (selected only if this step
        // maps it as an output). This is why Add Field never offers BO params — they are always here.
        const reconciled: ICRUDFormDiMetadata = reconcileFormBoParams(
          base,
          this.businessObjectParams,
          getStepOutputParameterIds(this.steps.find((s: IWFStep): boolean => s.id === stepId))
        );
        // A fresh server read is not an edit — it must not queue this step for Apply.
        this.setMetadata(stepId, reconciled, false);
        return true;
      }
      return false;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    } finally {
      this.setState({ loadingStepId: null });
    }
  };

  /**
   * A managed screen already has persisted form metadata — read it so prior edits survive. An
   * unbound / unmanaged step derives a fresh form from the process's Business Object.
   */
  private fetchStepMetadata = async (processId: string, stepId: string): Promise<ICRUDFormDiMetadata | undefined> => {
    const bound: IProcessStepScreensInfo | undefined = this.boundScreen(stepId);
    let response: AxiosResponse<ICRUDFormDiMetadata> | undefined;
    if (bound?.screenId && bound.isManaged) {
      response = await WorkflowScreenService.GetFormMetadataOfScreen(bound.screenId, { processId, stepId });
    } else {
      response = await FormService.GetFormMetadataFromWorkflowBo({ processId, stepId });
    }
    return response?.data;
  };

  /**
   * Store a step's form metadata. `isDirty: true` marks a USER edit (the step will be sent on
   * Apply); `isDirty: false` is a server copy (fresh load or adopted apply response) and CLEARS the
   * dirty mark — the local form now matches what's persisted, so there's nothing to apply.
   */
  private setMetadata = (stepId: string, formMetadata: ICRUDFormDiMetadata, isDirty: boolean): void => {
    const processId: string | undefined = this.workflow?.id;
    if (!processId) return;
    // Single choke point for every metadata copy entering the tab (fresh loads, adopted apply
    // responses, user edits): sections written before the wire model carried an id arrive with
    // `id: undefined` and would be invisible to id-based targeting — backfill here.
    const withIds: ICRUDFormDiMetadata = ensureFormSectionIds(formMetadata);
    this.setState((prev: IFormsTabState): Pick<IFormsTabState, "metadataByStep" | "dirtySteps"> => {
      const next: IFormMetadataByStep[] = prev.metadataByStep.filter(
        (entry: IFormMetadataByStep): boolean => entry.stepId !== stepId
      );
      next.push({ processId, stepId, formMetadata: withIds });
      const dirtySteps: Set<string> = new Set(prev.dirtySteps);
      if (isDirty) {
        dirtySteps.add(stepId);
      } else {
        dirtySteps.delete(stepId);
      }
      return { metadataByStep: next, dirtySteps };
    });
  };

  /** Sidebar label + status icon for a step, derived from its {@link stepState}. */
  private resolveScreenLabel = (step: IWFStep): IStepScreenLabel => {
    const state: StepScreenState = this.stepState(step.id);
    switch (state) {
      case "managed":
      case "unmanaged":
        return { text: this.screenName(this.boundScreen(step.id)?.screenId), state };
      case "draft":
        return { text: "Draft Screen", state };
      case "pending":
        return { text: this.screenName(this.pendingFor(step.id)?.screenId), state };
      default:
        return { text: "No Screen", state: "none" };
    }
  };

  /**
   * Actions a SelectBox / MultiSelect field may draw its options from.
   *
   * Project-wide on purpose: the picker has to work while the target screen is CLOSED (this tab
   * lives in the process editor), so the screen-scoped list the property panel uses is not
   * available. The visibility half reuses `isActionDefinitionVisible` — the platform's single
   * source of truth, shared with the property panel's action picker — so this surface cannot
   * drift from that one.
   */
  private get optionSourceActions(): IActionDefinitionDto[] {
    const sd = this.props.sd;
    const filterContext: ActionFilterContext = {
      dashboardState: sd.dashboardState,
      projectAccess: sd.project?.projectAccess,
      isBleServiceDataSource:
        sd.dashboardState === DashboardState.dataDiagramScheme &&
        sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService,
      // Restrictive rather than permissive: this only hides the Kuika-internal Invers category,
      // none of which returns a list, so nothing a user could legitimately pick is withheld.
      isUserKuikaDeveloper: false,
      isCurrentProcessScreenRole: !!sd.project?.processStepScreenIds?.some(
        (mapping: IProcessStepScreensInfo): boolean => mapping.screenId === sd.sdId
      )
    };
    return listSelectableOptionActions(sd.actionDefinitions, filterContext);
  }

  private get businessObjectParams(): IWFParameter[] {
    return (this.workflow?.parameters ?? []).filter((p: IWFParameter): boolean => p.isBusinessObject);
  }

  private get boParamIds(): Set<string> {
    return new Set(this.businessObjectParams.map((p: IWFParameter): string => p.id));
  }

  /**
   * Internal parameters not yet on the current form, offered in the Add Field picker. Business Object
   * params are excluded — they are always present on the form, so Add Field only brings in internal
   * parameters (custom, unbound fields are added via the section's "Add custom field").
   */
  private get addableParams(): IAddFieldParamOption[] {
    const metadata: ICRUDFormDiMetadata | undefined = this.getMetadataFor(this.state.selectedStepId ?? "");
    const present: Set<string> = new Set(
      (metadata?.sections ?? [])
        .flatMap((section): ICRUDFormField[] => section.items)
        .map((field: ICRUDFormField): string | undefined => field.linkedWorkflowBoParameterId)
        .filter((id): id is string => !!id)
    );
    return (this.workflow?.parameters ?? [])
      .filter((p: IWFParameter): boolean => !p.isBusinessObject && !present.has(p.id))
      .map((p: IWFParameter): IAddFieldParamOption => ({
        id: p.id,
        name: p.name,
        typeName: p.dataType,
        isBusinessObject: p.isBusinessObject
      }));
  }

  private handleAddFields = (paramIds: string[]): void => {
    const stepId: string | null = this.state.selectedStepId;
    const sectionId: string | null = this.state.addFieldSectionId;
    const metadata: ICRUDFormDiMetadata | undefined = stepId ? this.getMetadataFor(stepId) : undefined;
    const target: IFormSection | undefined = metadata?.sections.find((s: IFormSection): boolean => s.id === sectionId);
    if (!stepId || !metadata || !target) {
      this.setState({ addFieldOpen: false, addFieldSectionId: null });
      return;
    }
    const params: Map<string, IWFParameter> = new Map(
      (this.workflow?.parameters ?? []).map((p: IWFParameter): [string, IWFParameter] => [p.id, p])
    );
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    const nextTarget: IFormSection | undefined = next.sections.find((s: IFormSection): boolean => s.id === sectionId);
    if (!nextTarget) {
      this.setState({ addFieldOpen: false, addFieldSectionId: null });
      return;
    }
    for (const paramId of paramIds) {
      const param: IWFParameter | undefined = params.get(paramId);
      if (!param) continue;
      nextTarget.items.push(this.buildFieldFromParam(param));
    }
    this.setMetadata(stepId, next, true);
    this.setState({ addFieldOpen: false, addFieldSectionId: null });
  };

  /**
   * Sets the default selection for a fresh draft form. Only the Business Object params this step maps
   * as **outputs** are pre-selected (and editable); every other BO param is left **unselected and
   * read-only** — a removable, read-only reference row the user can opt into. Applied only to newly
   * derived drafts; a managed screen keeps whatever selection the user previously saved. Non-BO
   * (internal / custom) fields are untouched.
   */
  private normalizeDraftFields = (metadata: ICRUDFormDiMetadata, stepId: string): ICRUDFormDiMetadata => {
    const step: IWFStep | undefined = this.steps.find((s: IWFStep): boolean => s.id === stepId);
    const outputIds: Set<string> = getStepOutputParameterIds(step);
    const boIds: Set<string> = this.boParamIds;
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    for (const section of next.sections) {
      for (const field of section.items) {
        const paramId: string | undefined = field.linkedWorkflowBoParameterId;
        if (!paramId || !boIds.has(paramId)) continue;
        if (outputIds.has(paramId)) {
          field.isEnabled = true;
          field.isReadonlyField = false;
        } else {
          field.isEnabled = false;
          field.isReadonlyField = true;
        }
      }
    }
    return next;
  };

  /** A newly added parameter field: read-only by default, element type inferred from the data type. */
  private buildFieldFromParam = (param: IWFParameter): ICRUDFormField => ({
    id: Guid.create().toString(),
    isEnabled: true,
    name: param.name,
    typeName: param.dataType,
    elementType: defaultElementTypeFor(param.dataType),
    isRequired: false,
    isReadonlyField: true,
    decimalScale: null,
    source: EFormFieldSource.WorkflowBusinessObjectParameter,
    linkedWorkflowBoParameterName: param.name,
    linkedWorkflowBoParameterId: param.id
  });

  /** Queue a step for the next Apply without touching its stored form metadata. */
  private markStepDirty = (stepId: string): void => {
    this.setState((prev: IFormsTabState): Pick<IFormsTabState, "dirtySteps"> => {
      if (prev.dirtySteps.has(stepId)) return { dirtySteps: prev.dirtySteps };
      const dirtySteps: Set<string> = new Set(prev.dirtySteps);
      dirtySteps.add(stepId);
      return { dirtySteps };
    });
  };

  /**
   * An interaction edit was persisted to the workflow. The step's managed screen renders those
   * interactions as generated buttons, and the backend rebuilds them ONLY when the step goes through
   * Apply Screens — so the step must become apply-eligible even though its form metadata is
   * untouched. Loads the metadata first when the step was never visited on the Workflow Screens
   * sub-tab (Apply sends `metadataByStep` entries; a dirty step without one would be skipped).
   * Unmanaged / screenless steps have no generated buttons — nothing to queue.
   */
  private handleInteractionsChanged = (stepId: string): void => {
    const state: StepScreenState = this.stepState(stepId);
    if (state !== "managed" && state !== "draft") return;
    if (!this.getMetadataFor(stepId)) {
      // Mark dirty AFTER the load lands: the fresh server copy stores itself as clean.
      void this.loadStepMetadata(stepId).then((loaded: boolean): void => {
        if (loaded) this.markStepDirty(stepId);
      });
      return;
    }
    this.markStepDirty(stepId);
  };

  private handleCancel = (): void => {
    const stepId: string | null = this.state.selectedStepId;
    if (!stepId) return;
    // Only a form-bearing step has edits to discard; reload it from the backend.
    const state: StepScreenState = this.stepState(stepId);
    if (state === "managed" || state === "draft") void this.loadStepMetadata(stepId);
  };

  /**
   * The BO form derivation (`GetFormMetadataFromWorkflowBo`) reads the PERSISTED workflow row on
   * the server — a step just drawn on the diagram may not have landed yet (diagram saves fire
   * un-awaited), which surfaced as "Step not found." on create. Sync the workflow first; the
   * wf-hash dedupe makes this a no-op when nothing changed. Resolves false (after surfacing the
   * error) when the save failed — the metadata load would only fail the same way.
   */
  private syncWorkflowBeforeDraft = async (): Promise<boolean> => {
    const wf: IWorkFlow | undefined = this.workflow;
    if (!wf) return true;
    try {
      await this.props.sd.saveWorkFlow(wf);
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  };

  /** Drop failed drafts again — a draft without metadata would sit on an endless skeleton. */
  private rollbackDrafts = (stepIds: readonly string[]): void => {
    if (stepIds.length === 0) return;
    this.setState((prev: IFormsTabState): Pick<IFormsTabState, "draftSteps"> => {
      const draftSteps: Set<string> = new Set(prev.draftSteps);
      for (const stepId of stepIds) draftSteps.delete(stepId);
      return { draftSteps };
    });
  };

  /** User chose to build a new managed form for a screenless step: fetch a fresh BO-based draft. */
  private handleCreateDraft = async (stepId: string): Promise<void> => {
    const processId: string | undefined = this.workflow?.id;
    if (!processId) return;
    // CREATE and "assign existing" are mutually exclusive — drop any pending selection for this step.
    this.setState((prev: IFormsTabState): Pick<IFormsTabState, "draftSteps" | "pendingAssignments"> => {
      const draftSteps: Set<string> = new Set(prev.draftSteps);
      draftSteps.add(stepId);
      return {
        draftSteps,
        pendingAssignments: prev.pendingAssignments.filter(
          (p: IPendingUnmanagedAssignment): boolean => p.stepId !== stepId
        )
      };
    });
    if (!this.getMetadataFor(stepId)) {
      const synced: boolean = await this.syncWorkflowBeforeDraft();
      let loaded: boolean = false;
      if (synced) {
        loaded = await this.loadStepMetadata(stepId);
      }
      // The failure itself was already surfaced by the sync/load exception handler.
      if (!loaded) this.rollbackDrafts([stepId]);
    }
  };

  /** Steps with nothing bound, drafted, or pending — the ones "Create All Screens" targets. */
  private get screenlessSteps(): IWFStep[] {
    return this.steps.filter((step: IWFStep): boolean => this.stepState(step.id) === "none");
  }

  /**
   * The legacy wizard's bulk create, scoped to this process (KOA-18273): draft a new managed form
   * for EVERY step that still has no screen, in one click. Mirrors {@link handleCreateDraft} per
   * step — syncs the workflow, marks them all as drafts, then loads each missing form metadata
   * sequentially (the same backend derivation, kept serial so the server isn't hammered). A step
   * whose load fails drops back to "No Screen" for a retry. Nothing is persisted here; the drafts
   * land with the next "Apply Screens", exactly like a single created draft.
   */
  private handleCreateAllScreens = async (): Promise<void> => {
    const missing: IWFStep[] = this.screenlessSteps;
    if (missing.length === 0 || this.state.isBulkCreating) return;
    this.setState((prev: IFormsTabState): Pick<IFormsTabState, "draftSteps" | "isBulkCreating"> => {
      const draftSteps: Set<string> = new Set(prev.draftSteps);
      for (const step of missing) draftSteps.add(step.id);
      return { draftSteps, isBulkCreating: true };
    });
    try {
      const synced: boolean = await this.syncWorkflowBeforeDraft();
      const failed: string[] = [];
      for (const step of missing) {
        if (this.getMetadataFor(step.id)) continue;
        if (!synced) {
          failed.push(step.id);
          continue;
        }
        const loaded: boolean = await this.loadStepMetadata(step.id);
        if (!loaded) failed.push(step.id);
      }
      this.rollbackDrafts(failed);
    } finally {
      this.setState({ isBulkCreating: false });
    }
  };

  /** User picked (or cleared) an existing screen to assign to a step as unmanaged. */
  private handleAssignExisting = (stepId: string, screenId: string | null): void => {
    const processId: string | undefined = this.workflow?.id;
    if (!processId) return;
    this.setState(
      (prev: IFormsTabState): Pick<IFormsTabState, "pendingAssignments" | "draftSteps" | "metadataByStep"> => {
        // A screen can only be pending on one step; drop any prior pending for this step or that screen.
        const pendingAssignments: IPendingUnmanagedAssignment[] = prev.pendingAssignments.filter(
          (p: IPendingUnmanagedAssignment): boolean => p.stepId !== stepId && p.screenId !== screenId
        );
        if (screenId) pendingAssignments.push({ processId, stepId, screenId });
        // Assigning an existing screen supersedes any draft the user had started for this step.
        const draftSteps: Set<string> = new Set(prev.draftSteps);
        let metadataByStep: IFormMetadataByStep[] = prev.metadataByStep;
        if (screenId) {
          draftSteps.delete(stepId);
          metadataByStep = prev.metadataByStep.filter((entry: IFormMetadataByStep): boolean => entry.stepId !== stepId);
        }
        return { pendingAssignments, draftSteps, metadataByStep };
      }
    );
  };

  /**
   * Links each pending existing screen to its step as **unmanaged** by writing the project's
   * `processStepScreenIds` (mirrors the legacy wizard). Reflects locally via `updateProject`, so no
   * disruptive reload is needed. Returns true when anything was applied.
   */
  private applyPendingUnmanaged = async (): Promise<boolean> => {
    const pending: IPendingUnmanagedAssignment[] = this.state.pendingAssignments;
    const project: IProject | undefined = this.props.sd.project;
    if (pending.length === 0 || !project) return false;

    const pendingScreenIds: Set<string> = new Set(pending.map((p: IPendingUnmanagedAssignment): string => p.screenId));
    const next: IProcessStepScreensInfo[] = structuredClone(project.processStepScreenIds ?? [])
      .filter((p: IProcessStepScreensInfo): boolean => !!(p.projectId && p.processId && p.stepId && p.screenId))
      // Each pending screen lives in exactly one step, so drop any prior binding for those screens.
      .filter((p: IProcessStepScreensInfo): boolean => !pendingScreenIds.has(p.screenId));
    for (const assignment of pending) {
      next.push({
        screenId: assignment.screenId,
        projectId: project.id,
        processId: assignment.processId,
        stepId: assignment.stepId,
        isManaged: false
      });
    }

    project.processStepScreenIds = next;
    await this.props.sd.updateProject(project);
    await this.props.sd.setScreenList();
    this.setState({ pendingAssignments: [] });
    return true;
  };

  /**
   * Apply: first link pending existing screens (unmanaged, via the project), then generate/update the
   * managed forms held in `metadataByStep` (managed screens + created drafts) through the same backend
   * the legacy wizard used.
   *
   * The response is authoritative and must NOT be discarded (that was the bug): every edited step
   * carries an `isSuccess` flag + the server's canonical `formMetadata` (post `SyncFormMetadataWithWorkflow`).
   * We surface failures, adopt the canonical metadata so the in-tab view matches what was persisted, and
   * refresh the project row so a newly-created draft's managed binding lands locally — via
   * `refreshProjectFromServer` (re-reads the project only), NOT the legacy `reloadScreen` that tears
   * down the open process. Without this, an applied draft's changes appeared reverted on the next visit
   * because the local project never learned the new screen and the step fell back to "No Screen".
   */
  private handleApplyScreens = async (): Promise<void> => {
    const processId: string | undefined = this.workflow?.id;
    const projectId: string | undefined = this.props.sd.project?.id;
    if (!processId) return;
    this.setState({ isApplying: true, applyProgress: null });

    // Only the forms the user actually edited (dirty) or explicitly created (draft) are applied.
    // `metadataByStep` also caches every step the user merely selected in the sidebar — sending
    // those would regenerate screens the user never touched.
    const formMetadataBySteps: IFormMetadataByStep[] = this.buildApplyEntries();
    const appliedScreenIds: Set<string> = this.screenIdsFor(formMetadataBySteps);

    // The backend regen whole-tree-writes every applied managed screen — illegal while a live
    // collab session owns one. If the screen open in the UI designer is among them, suspend OUR
    // session first (flush pending ops, await the server-side leave), or the backend's collab
    // guard would fail the step against the user's own tab. Other users' live sessions still
    // fail that step gracefully server-side. Resumed in `finally` after the in-place refresh, so
    // the rejoin seeds a fresh session from the regenerated tree.
    const openScreenId: string = this.props.sd.sdId;
    const suspendedCollab: boolean =
      !!openScreenId && this.props.sd.collabSessionActive && appliedScreenIds.has(openScreenId);

    try {
      // Interaction commits fire saveWorkFlow WITHOUT awaiting (blur-time UX), and the backend
      // regenerates each step's buttons FROM the persisted workflow row — so sync the workflow
      // first or a quick Apply races the save and rebuilds from stale interactions. The wf-hash
      // dedupe makes this a no-op when nothing changed; under live collab it flushes queued ops.
      const wf: IWorkFlow | undefined = this.workflow;
      if (wf) await this.props.sd.saveWorkFlow(wf);

      if (suspendedCollab) await this.props.sd.suspendCollabForAgentBuild();

      const appliedPending: boolean = await this.applyPendingUnmanaged();

      if (formMetadataBySteps.length === 0 && !appliedPending) {
        KMainFunctions.displayWarningNotification(
          "Nothing to apply — edit a form, or create or assign a screen first."
        );
        return;
      }

      // The regeneration is the long part — a stream the user watches step by step on the overlay —
      // so one toast carries it from "Applying" to its outcome, partial failures included.
      const work: Promise<void> = this.applyScreensAndRefresh(
        formMetadataBySteps,
        processId,
        projectId,
        openScreenId,
        appliedScreenIds
      );
      toastService.promise(work, {
        loading: "Applying screens…",
        success: "Workflow screens updated",
        error: failureMessageOr("Screens could not be updated.")
      });
      // The toast has said how it ended; the catch below is for the preparation steps only.
      await work.catch((): void => undefined);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      // Rejoin AFTER the refreshed tree landed: the backend dropped the old (stale) session, so
      // the first joiner seeds the new one — that seed must be the regenerated tree.
      if (suspendedCollab) await this.props.sd.resumeCollabAfterAgentBuild(openScreenId);
      this.setState({ isApplying: false, applyProgress: null });
    }
  };

  /**
   * The regeneration itself, after `handleApplyScreens` has synced the workflow and found
   * something to apply. Rejects when any step failed, with the message the toast shows — a
   * partial failure is a failure of the Apply, even though the other screens did update.
   */
  private applyScreensAndRefresh = async (
    formMetadataBySteps: IFormMetadataByStep[],
    processId: string,
    projectId: string | undefined,
    openScreenId: string,
    appliedScreenIds: Set<string>
  ): Promise<void> => {
    // A created draft (a step the user is building a NEW managed screen for) needs the project
    // refreshed afterwards so its freshly-written binding lands locally. A pure edit of an
    // already-managed screen does not — skip the extra project fetch in that common case.
    const appliedDraft: boolean = formMetadataBySteps.some((entry: IFormMetadataByStep): boolean =>
      this.state.draftSteps.has(entry.stepId)
    );

    let failures: IApplyStepFailure[] = [];
    if (formMetadataBySteps.length > 0) {
      // `onlyProvidedSteps`: apply just this process's edited steps — do NOT let the backend
      // reprocess every managed screen in the project (slow, and it stranded our own edits at
      // the tail of the stream where a timeout dropped them).
      const response: IUpdateWorkflowScreensResponse = await WorkflowScreenService.UpdateWorkflowScreensWithProgress(
        {
          formMetadataBySteps,
          onlyProvidedSteps: true
        },
        this.handleApplyProgress
      );
      failures = this.adoptApplyResponse(
        response,
        processId,
        formMetadataBySteps.map((entry: IFormMetadataByStep): string => entry.stepId)
      );
    }

    // Land a created draft's new managed binding locally, without the disruptive canvas reload
    // that would leave the open process.
    if (appliedDraft && projectId) await this.props.sd.refreshProjectFromServer(projectId);
    await this.props.sd.setScreenList();

    // If the screen currently open in the UI designer is one whose form we just regenerated, refresh
    // its tree in place so the change shows without a full page reload. Navigating to a DIFFERENT
    // screen already re-fetches; only the already-open screen stays cached. No-op otherwise.
    if (openScreenId && appliedScreenIds.has(openScreenId)) {
      await this.props.sd.refreshOpenScreenDesign(openScreenId);
    }

    if (failures.length > 0) {
      const names: string = failures.map((f: IApplyStepFailure): string => this.stepDisplayName(f.stepId)).join(", ");
      throw new Error(`Some screens could not be updated (${names}). ${failures[0].message ?? ""}`.trim());
    }

    // Every step landed — now the one process-level question an Apply can raise.
    this.refreshStartScreenPrompt();
  };

  /** Per-step SSE progress → the blocking overlay's caption ("Updating Approve Request (2/5)…"). */
  private handleApplyProgress = (progress: IUpdateWorkflowScreensProgress): void => {
    if (!this.state.isApplying) return;
    if (progress?.type !== "progress" && progress?.type !== "step_completed") return;
    const name: string = progress.stepName || "screens";
    const counter: string =
      progress.currentStep && progress.totalSteps ? ` (${progress.currentStep}/${progress.totalSteps})` : "";
    this.setState({ applyProgress: `Updating ${name}${counter}…` });
  };

  /**
   * After every step of an Apply landed: a process that declares input parameters but that no
   * StartProcess action in the project targets can never run, so offer to generate its start
   * screen — the legacy wizard's post-save prompt (KOA-18746). Read-only on the trigger index; the
   * sync hooks own its upkeep (see `needsStartScreen` for why it is not swept here).
   */
  private refreshStartScreenPrompt = (): void => {
    const workflow: IWorkFlow | undefined = this.workflow;
    const triggers: IStartProcessTrigger[] | undefined = workflow
      ? this.props.sd.project?.startProcessTriggers?.[workflow.id]
      : undefined;
    this.setState({ startScreenPromptOpen: needsStartScreen(workflow, triggers) });
  };

  private dismissStartScreenPrompt = (): void => {
    this.setState({ startScreenPromptOpen: false });
  };

  /**
   * Yes: the backend materialises a form + Start Process button screen bound to the process's
   * input parameters and writes its StartProcess trigger into project.json itself. Re-read the
   * project (a plain fetch — not the canvas reload, so the user stays in this process) so the local
   * index agrees without pushing a full-overwrite patch from a possibly stale local copy, then
   * refresh the screen list so the new screen shows in the Screens panel. Nothing navigates: the
   * user keeps working on this process's steps.
   */
  private handleGenerateStartScreen = async (): Promise<void> => {
    const workflow: IWorkFlow | undefined = this.workflow;
    const projectId: string | undefined = this.props.sd.project?.id;
    if (!workflow?.id || this.state.isGeneratingStartScreen) return;
    this.setState({ isGeneratingStartScreen: true });
    const screenName: string | undefined = startScreenNameFor(workflow);
    try {
      const res: AxiosResponse<ICreateWorkflowStartScreenResponse> =
        await WorkflowScreenService.CreateWorkflowStartScreen({ processId: workflow.id, screenName });
      // The service reports its own failure (exceptionHandler) and hands the error back as the response.
      if (!res || res.status !== 200 || !res.data?.screenId) return;
      if (projectId) await this.props.sd.refreshProjectFromServer(projectId);
      await this.props.sd.setScreenList();
      this.setState({ startScreenPromptOpen: false });
      toastService.success(screenName ? `Start screen created: ${screenName}` : "Start screen created.");
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isGeneratingStartScreen: false });
    }
  };

  /**
   * Reconcile local state with the Apply response via {@link partitionApplyResults}: for the steps
   * THIS tab sent, adopt the server's canonical metadata (keeping the in-tab view and its
   * predefined ids in lockstep with what was saved) and return the failed steps — including steps
   * whose result slot came back null/missing (the stream leaves it unset on a step_error), so a
   * dropped step is never mistaken for a success.
   */
  private adoptApplyResponse = (
    response: IUpdateWorkflowScreensResponse | undefined,
    processId: string,
    sentStepIds: readonly string[]
  ): IApplyStepFailure[] => {
    const partition: IApplyResultPartition = partitionApplyResults(
      response?.formMetadataBySteps ?? [],
      processId,
      sentStepIds
    );
    for (const adoption of partition.adopted) {
      // Adopting the canonical server copy also clears the step's dirty mark — it's applied now.
      this.setMetadata(adoption.stepId, adoption.formMetadata, false);
    }
    if (partition.adopted.length > 0) {
      // An applied draft is a managed screen now — drop the draft mark so the next Apply doesn't
      // re-send it untouched. Failed drafts stay marked so a retry still sends them.
      const adoptedStepIds: Set<string> = new Set(
        partition.adopted.map((adoption: IApplyStepAdoption): string => adoption.stepId)
      );
      this.setState((prev: IFormsTabState): Pick<IFormsTabState, "draftSteps"> => {
        const draftSteps: Set<string> = new Set(prev.draftSteps);
        adoptedStepIds.forEach((stepId: string): void => {
          draftSteps.delete(stepId);
        });
        return { draftSteps };
      });
    }
    return partition.failures;
  };

  /** A step's name for user-facing messages, falling back to a short id when the step is gone. */
  private stepDisplayName = (stepId: string): string => {
    const step: IWFStep | undefined = this.steps.find((s: IWFStep): boolean => s.id === stepId);
    return step?.name || `Step ${stepId.slice(0, 8)}`;
  };

  private renderContent = () => {
    const steps: IWFStep[] = this.steps;
    if (steps.length === 0) {
      return (
        <div className="kuika-wf-forms__empty">
          <KuikaEmptyState
            variant="page"
            svgName="checklist"
            svgType="common"
            customName="No user tasks in this process"
            description="Add a user task (screen interaction) to the diagram to generate its form here."
          />
        </div>
      );
    }

    const isInteractions: boolean = this.state.subTab === SUBTAB_INTERACTIONS;
    return (
      <div className="kuika-wf-forms__body">
        <FormsTabSidebar
          steps={steps}
          selectedStepId={this.state.selectedStepId}
          onSelect={this.selectStep}
          resolveScreenLabel={this.resolveScreenLabel}
        />
        <div className="kuika-wf-forms__content">
          {isInteractions ? (
            <FormsTabInteractions
              selectedStepId={this.state.selectedStepId}
              onChanged={this.handleInteractionsChanged}
            />
          ) : (
            this.renderStepBody()
          )}
        </div>
      </div>
    );
  };

  /** The right-hand body for the selected step: the screen builder, or a create/assign/unmanaged panel. */
  private renderStepBody = () => {
    const stepId: string | null = this.state.selectedStepId;
    if (!stepId) return null;
    const state: StepScreenState = this.stepState(stepId);

    // A draft has no screen yet — nothing to outline, and the field table IS the whole editor
    // until Apply Screens creates one.
    if (state === "draft") return this.renderForm(stepId);
    if (state === "managed") return this.renderForm(stepId);
    if (state === "unmanaged") return this.renderUnmanagedPanel(stepId);
    if (state === "pending") return this.renderPendingPanel(stepId);
    return this.renderNoScreenPanel(stepId);
  };

  private renderForm = (stepId: string) => {
    const metadata: ICRUDFormDiMetadata | undefined = this.getMetadataFor(stepId);
    const isLoading: boolean = this.state.loadingStepId === stepId;
    if (isLoading || !metadata) {
      // A skeleton of the section table it is fetching (title + field rows + a button-ish
      // bar), not a text label: this load runs seconds, and a static "Loading…" reads as
      // a hang. aria-busy on the region announces the state; the bars are decorative.
      return (
        <div className="kuika-wf-forms__loading" aria-busy="true" data-testid="processFormsLoading">
          <KuikaSkeletonTable rows={4} header />
          <KuikaSkeletonTable rows={1} rowHeight={28} className="kuika-wf-forms__loading-action" />
        </div>
      );
    }
    return (
      <FormsTabForm
        metadata={metadata}
        formTitle={this.formTitle()}
        outputParams={getStepOutputParams(this.workflow, this.selectedStep)}
        outputParamIds={getStepOutputParameterIds(this.selectedStep)}
        boParamIds={this.boParamIds}
        tableTypeNames={[]}
        optionSourceActions={this.optionSourceActions}
        isDraft={this.stepState(stepId) === "draft"}
        onChange={(updated: ICRUDFormDiMetadata): void => this.setMetadata(stepId, updated, true)}
        onOpenAddField={(sectionId: string): void =>
          this.setState({ addFieldOpen: true, addFieldSectionId: sectionId })
        }
      />
    );
  };

  private renderAssignDropdown = (stepId: string, value: string | undefined) => {
    const processId: string | undefined = this.workflow?.id;
    return (
      <AssignScreenDropdown
        value={value}
        onChange={(screenId: string | null): void => this.handleAssignExisting(stepId, screenId)}
        currentProcessId={processId ?? null}
        currentStepId={stepId}
        pendingAssignments={this.state.pendingAssignments}
      />
    );
  };

  /** Screenless step: choose to create a new form, or assign an existing (unmanaged) screen. */
  private renderNoScreenPanel = (stepId: string) => (
    <div className="kuika-wf-forms__state" data-testid="processFormsNoScreen">
      <KuikaSvg name="screen-share" width={40} height={40} color="var(--dark-6)" />
      <span className="kuika-wf-forms__state-title">No screen for this step</span>
      <span className="kuika-wf-forms__state-desc">
        Create a new form for this step, or assign an existing screen — an assigned screen stays unmanaged and its form
        is edited on its own screen.
      </span>
      <div className="kuika-wf-forms__state-actions">
        <KuikaButton
          type="primary"
          uppercase
          onClick={(): void => void this.handleCreateDraft(stepId)}
          data-testid="processFormsCreateScreen"
        >
          Create Screen
        </KuikaButton>
        <span className="kuika-wf-forms__state-or">or</span>
        <span className="kuika-wf-forms__state-dropdown">{this.renderAssignDropdown(stepId, undefined)}</span>
      </div>
    </div>
  );

  /** An existing screen is selected but not yet linked — links (unmanaged) on Apply Screens. */
  private renderPendingPanel = (stepId: string) => {
    const pending: IPendingUnmanagedAssignment | undefined = this.pendingFor(stepId);
    return (
      <div className="kuika-wf-forms__state" data-testid="processFormsPending">
        <KuikaSvg name="clock" width={40} height={40} color="var(--orange-6, #f59e0b)" />
        <span className="kuika-wf-forms__state-title">Existing screen pending</span>
        <span className="kuika-wf-forms__state-desc">
          <strong>{this.screenName(pending?.screenId)}</strong> will be linked to this step as an unmanaged screen when
          you click <strong>Apply Screens</strong>.
        </span>
        <div className="kuika-wf-forms__state-actions">
          <span className="kuika-wf-forms__state-dropdown">{this.renderAssignDropdown(stepId, pending?.screenId)}</span>
          <span className="kuika-wf-forms__state-or">or</span>
          <KuikaButton
            type="secondary"
            uppercase
            onClick={(): void => void this.handleCreateDraft(stepId)}
            data-testid="processFormsCreateInstead"
          >
            Create Instead
          </KuikaButton>
        </div>
      </div>
    );
  };

  /** A persisted unmanaged screen — not editable here; the user may swap it for another. */
  private renderUnmanagedPanel = (stepId: string) => {
    const bound: IProcessStepScreensInfo | undefined = this.boundScreen(stepId);
    const pending: IPendingUnmanagedAssignment | undefined = this.pendingFor(stepId);
    return (
      <div className="kuika-wf-forms__state" data-testid="processFormsUnmanaged">
        <KuikaSvg name="screen-share" width={40} height={40} color="var(--dark-9)" />
        <span className="kuika-wf-forms__state-title">Unmanaged screen</span>
        <span className="kuika-wf-forms__state-desc">
          <strong>{this.screenName(bound?.screenId)}</strong> is assigned to this step but not generated here, so its
          form isn&apos;t editable in this tab. You can assign a different screen.
        </span>
        <div className="kuika-wf-forms__state-actions">
          <span className="kuika-wf-forms__state-dropdown">
            {this.renderAssignDropdown(stepId, pending?.screenId ?? bound?.screenId)}
          </span>
        </div>
        {pending && (
          <span className="kuika-wf-forms__state-note">
            The new screen links when you click <strong>Apply Screens</strong>.
          </span>
        )}
      </div>
    );
  };

  private formTitle = (): string => {
    const step: IWFStep | undefined = this.selectedStep;
    if (!step) return "Form";
    const bound: IProcessStepScreensInfo | undefined = this.boundScreen(step.id);
    if (bound?.screenId) {
      const screen: IScreenDesignInfo | undefined = this.props.sd.screenList?.find(
        (s: IScreenDesignInfo): boolean => s.id === bound.screenId
      );
      if (screen?.name) return screen.name;
    }
    return `${step.name || "Step"} Form`;
  };

  /**
   * The post-Apply offer to generate a start screen. Process-level — so it sits between the
   * toolbar and the card, spans the rail and the form, and survives a step change in the rail. The
   * box tints the icon through its own `--kuika-svg-color`, so the glyph carries no colour of its own.
   */
  private renderStartScreenPrompt = (): JSX.Element => {
    const isGenerating: boolean = this.state.isGeneratingStartScreen;
    return (
      <div className="kuika-wf-forms__start-screen-prompt" role="status" data-testid="processFormsStartScreenPrompt">
        <KuikaInfoBox
          color="yellow"
          leftUnit={
            <div className="kuika-flex--gap-xs">
              <KuikaSvg name="alert-triangle" width={16} height={16} />
              <KuikaSmallText>
                This process takes input parameters and is not started from anywhere in this project. Generate a start
                screen for it?
              </KuikaSmallText>
            </div>
          }
          rightUnit={
            <span className="kuika-flex-end--gap">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                disabled={isGenerating}
                onClick={this.dismissStartScreenPrompt}
                data-testid="processFormsStartScreenNo"
              >
                No
              </KuikaButton>
              <KuikaButton
                type="primary"
                size="sm"
                loading={isGenerating}
                onClick={(): void => void this.handleGenerateStartScreen()}
                data-testid="processFormsStartScreenYes"
              >
                Yes
              </KuikaButton>
            </span>
          }
        />
      </div>
    );
  };

  /** A sub-tab label with a trailing count badge (stable `value` is kept separate in the options). */
  private subTabLabel = (text: string, count: number): JSX.Element => (
    <span className="kuika-wf-forms__subtab">
      {text}
      <span className="kuika-wf-forms__subtab-count">{count}</span>
    </span>
  );

  render() {
    if (!this.workflow) {
      return (
        <div className="kuika-wf-forms kuika-wf-forms--empty" data-testid="processFormsTab">
          <KuikaEmptyState
            variant="page"
            svgName="checklist"
            svgType="common"
            customName="No process open"
            description="Open a process to manage its step forms."
          />
        </div>
      );
    }

    // What the next Apply will actually send: edited/draft step forms + pending screen assignments.
    // Surfaced on the button so "will my change apply?" is answerable at a glance — an interaction
    // or form edit bumps the count, merely browsing steps does not.
    const pendingApplyCount: number = this.pendingApplyCount;

    return (
      <div className="kuika-wf-forms" data-testid="processFormsTab">
        <div className="kuika-wf-forms__toolbar">
          <KuikaTabs
            className="kuika-wf-forms__subtabs"
            size="lg"
            options={[
              {
                value: SUBTAB_SCREENS,
                label: this.subTabLabel(SUBTAB_SCREENS, this.mappedScreenCount),
                dataTestId: subTabTestId(SUBTAB_SCREENS)
              },
              {
                value: SUBTAB_INTERACTIONS,
                label: this.subTabLabel(SUBTAB_INTERACTIONS, this.interactionCount),
                dataTestId: subTabTestId(SUBTAB_INTERACTIONS)
              }
            ]}
            value={this.state.subTab}
            onChange={(subTab: string): void => this.setState({ subTab })}
            dataTestId="processFormsSubtabs"
          />
          <span className="kuika-wf-forms__toolbar-actions">
            {this.screenlessSteps.length > 0 && (
              <KuikaButton
                type="secondary"
                uppercase
                loading={this.state.isBulkCreating}
                disabled={this.state.isApplying}
                onClick={(): void => void this.handleCreateAllScreens()}
                data-testid="processFormsCreateAll"
              >
                <KuikaSvg name="plus" width={14} height={14} />
                Create All Screens ({this.screenlessSteps.length})
              </KuikaButton>
            )}
            <KuikaTooltip content="Workflow screen settings (project-wide)">
              <KuikaButton
                type="tertiary"
                isIcon
                disabled={this.state.isApplying}
                onClick={(): void => this.setState({ screenSettingsOpen: true })}
                aria-label="Workflow screen settings"
                data-testid="processFormsScreenSettings"
              >
                <KuikaSvg name="settings" width={16} height={16} />
              </KuikaButton>
            </KuikaTooltip>
            <KuikaButton
              type="text"
              uppercase
              onClick={this.handleCancel}
              disabled={this.state.isApplying}
              data-testid="processFormsCancel"
            >
              Cancel
            </KuikaButton>
            <KuikaButton
              type="primary"
              uppercase
              loading={this.state.isApplying}
              onClick={(): void => void this.handleApplyScreens()}
              data-testid="processFormsApply"
            >
              Apply Screens
              {pendingApplyCount > 0 ? ` (${pendingApplyCount})` : ""}
            </KuikaButton>
          </span>
        </div>

        {this.state.startScreenPromptOpen && this.renderStartScreenPrompt()}

        {this.renderContent()}

        {/* Full-viewport blocking overlay while applying: the backend is rewriting the managed
            screens (and the open screen's collab session is suspended), so ANY concurrent edit —
            canvas, other tools — would race the regen and be lost. KuikaSpinner's overlay is
            position:fixed and swallows pointer events; the caption follows the stream progress. */}
        <KuikaSpinner
          spinning={this.state.isApplying}
          size="large"
          label={this.state.applyProgress ?? "Applying screens…"}
        />

        <FormsTabAddFieldModal
          isOpen={this.state.addFieldOpen}
          onClose={(): void => this.setState({ addFieldOpen: false, addFieldSectionId: null })}
          options={this.addableParams}
          onSave={this.handleAddFields}
        />

        <FormsTabScreenSettings
          isOpen={this.state.screenSettingsOpen}
          value={this.props.sd.project?.workflowScreensSettings}
          onClose={(): void => this.setState({ screenSettingsOpen: false })}
          onSave={this.handleSaveScreenSettings}
        />
      </div>
    );
  }

  checkNeedRender = (): boolean => true;
}

const formsTab = withSD<IFormsTabProps>(FormsTab);
export default formsTab;
